import { useEffect, useState, useCallback, useMemo } from 'react';
import { useScrollHeader } from '../utils/hooks';
import api from '../services/api';
import MathText from '../components/MathText';
import { canUserManageContent, canUserDeleteContent, canUserManageFolder, canUserCreateInFolder } from '../utils/rbac';
import FolderDetailModal from '../components/FolderDetailModal';
import CreateFolderModal from '../components/CreateFolderModal';
import BulkUploadModal from '../components/BulkUploadModal';
import SEO from '../components/SEO';
import Modal from '../components/Modal';

/* ── Build flat folder list into a tree ── */
const buildTree = (folders) => {
  const map = new Map(folders.map(f => [f._id, { ...f, children: [], level: 0 }]));
  const roots = [];
  for (const node of map.values()) {
    const parentId = node.parent?._id ?? node.parent;
    if (parentId && map.has(parentId.toString())) {
      const parent = map.get(parentId.toString());
      node.level = parent.level + 1;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
};

/* ── Recursive sidebar folder node ── */
const SidebarFolder = ({ 
  node, selectedId, onSelect, expanded, onToggle, getItemCount,
  draggedFolder, dragOverId, onDragStart, onDragOver, onDragLeave, onDrop,
  user, folders
}) => {
  const isSelected = selectedId === node._id;
  const isExpanded = expanded.has(node._id);
  const hasChildren = node.children && node.children.length > 0;
  const count = getItemCount(node._id);

  return (
    <div>
      <div
        className={`sidebar-folder${isSelected ? ' sidebar-folder-selected' : ''}${dragOverId === node._id ? ' drag-over' : ''}`}
        style={{ paddingLeft: `${20 + node.level * 16}px`, display: 'flex', alignItems: 'center' }}
        draggable={canUserManageFolder(user, node, folders)}
        onDragStart={(e) => onDragStart(e, node)}
        onDragOver={(e) => onDragOver(e, node._id)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, node._id)}
        onClick={() => {
          onSelect(node);
          if (hasChildren) onToggle(node._id);
        }}
      >
        {hasChildren ? (
          <button
            className="sidebar-toggle"
            onClick={(e) => { e.stopPropagation(); onToggle(node._id); }}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 4px', color: 'inherit' }}
          >
            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: '0.7rem' }} />
          </button>
        ) : (
          <span style={{ width: '18px' }} />
        )}
        <i className={`fas fa-folder${isExpanded ? '-open' : ''}`} style={{ marginRight: '10px', opacity: 0.7 }} />
        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <MathText key={node.title} text={node.title} />
        </span>
        {count > 0 && (
          <span style={{ fontSize: '0.7rem', opacity: 0.6, marginLeft: '8px' }}>{count}</span>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div className="sidebar-children">
          {node.children.map(child => (
            <SidebarFolder
              key={child._id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              expanded={expanded}
              onToggle={onToggle}
              getItemCount={getItemCount}
              draggedFolder={draggedFolder}
              dragOverId={dragOverId}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              user={user}
              folders={folders}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main BrowsePage ── */
const BrowsePage = ({ 
  user, 
  onLogout, 
  onTakeTest, 
  onCreateContent, 
  onEditContent, 
  onGoToBrowse, 
  initialFolderId, 
  onFolderSelect,
  onViewDocument
}) => {
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('studymate_view_mode') || 'grid');
  const [viewSelectorOpen, setViewSelectorOpen] = useState(false);

  useEffect(() => {
    if (!viewSelectorOpen) return;
    const handleClose = () => setViewSelectorOpen(false);
    window.addEventListener('click', handleClose);
    return () => window.removeEventListener('click', handleClose);
  }, [viewSelectorOpen]);


  
  // Folder Management State
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderTitle, setEditFolderTitle] = useState('');
  const [editFolderDescription, setEditFolderDescription] = useState('');
  const [editFolderParentId, setEditFolderParentId] = useState('');
  const [editFolderReleased, setEditFolderReleased] = useState(false);
  const [editFolderAllowedGroups, setEditFolderAllowedGroups] = useState([]);
  const [editFolderAllowedUsers, setEditFolderAllowedUsers] = useState([]);
  const [editFolderIsCollege, setEditFolderIsCollege] = useState(false);
  const [editFolderError, setEditFolderError] = useState('');
  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [draggedFolder, setDraggedFolder] = useState(null);
  const [draggedPaper, setDraggedPaper] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isHeaderVisible, setIsHeaderVisible] = useScrollHeader({ scrollContainerId: 'browse-content-area' });
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const isStaff = ['super_user', 'college_admin', 'teacher'].includes(user.role);
        const folderEndpoint = isStaff ? '/folders/editfolders' : '/folders';
        
        const [fRes, dRes, eRes, uRes, gRes] = await Promise.all([
          api.get(folderEndpoint),
          api.get('/documents'),
          api.get('/exams'),
          isStaff ? api.get('/users') : Promise.resolve({ data: [] }),
          isStaff ? api.get('/user-groups') : Promise.resolve({ data: [] }),
        ]);

        if (
          !Array.isArray(fRes.data) || 
          !Array.isArray(dRes.data) || 
          !Array.isArray(eRes.data) ||
          !Array.isArray(uRes.data) ||
          !Array.isArray(gRes.data)
        ) {
          throw new Error('API returned invalid data format');
        }

        setFolders(fRes.data);
        setDocuments(dRes.data);
        setExams(eRes.data);
        setUsers(uRes.data);
        setUserGroups(gRes.data);

        if (initialFolderId) {
          const match = fRes.data.find(f => f._id === initialFolderId);
          if (match) {
            setSelectedFolder(match);
            // Auto-expand parents
            setExpanded(prev => {
              const next = new Set(prev);
              let current = match;
              while (current) {
                next.add(current._id);
                const parentId = current.parent?._id ?? current.parent;
                current = parentId ? fRes.data.find(f => f._id === parentId.toString()) : null;
              }
              return next;
            });
          }
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Unable to load content');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.role, initialFolderId]);

  // ── Sync selected folder when initialFolderId changes AFTER data is already loaded ──
  useEffect(() => {
    if (loading || !initialFolderId || folders.length === 0) return;
    const match = folders.find(f => f._id === initialFolderId);
    if (match) {
      setSelectedFolder(match);
      setExpanded(prev => {
        const next = new Set(prev);
        let current = match;
        while (current) {
          next.add(current._id);
          const parentId = current.parent?._id ?? current.parent;
          current = parentId ? folders.find(f => f._id === parentId.toString()) : null;
        }
        return next;
      });
    } else if (!initialFolderId) {
      setSelectedFolder(null);
    }
  }, [initialFolderId, folders, loading]);

  const handleToggle = useCallback((id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSelectFolder = useCallback((folder) => {
    setSelectedFolder(folder);
    setMobileDrawerOpen(false); // Close drawer on folder select
    if (onFolderSelect) onFolderSelect(folder ? folder._id : '');
  }, [onFolderSelect]);

  const handleDelete = async (itemType, item) => {
    // Open confirmation modal instead of native confirm
    setDeleteConfirm({ itemType, item });
  };

  const performDelete = async () => {
    if (!deleteConfirm) return;
    const { itemType, item } = deleteConfirm;
    try {
      const endpoint = itemType === 'Exam' ? '/exams' : '/documents';
      await api.delete(`${endpoint}/${item._id}`);
      if (itemType === 'Exam') setExams(prev => prev.filter(e => e._id !== item._id));
      else setDocuments(prev => prev.filter(d => d._id !== item._id));
      setDeleteConfirm(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete item');
      setDeleteConfirm(null);
    }
  };

  const handleToggleReleased = async (itemType, item) => {
    const newReleased = !item.released;
    const endpoint = itemType === 'Exam' ? '/exams' : '/documents';
    // Optimistic update
    if (itemType === 'Exam') {
      setExams(prev => prev.map(e => e._id === item._id ? { ...e, released: newReleased } : e));
    } else {
      setDocuments(prev => prev.map(d => d._id === item._id ? { ...d, released: newReleased } : d));
    }
    try {
      await api.put(`${endpoint}/${item._id}`, { released: newReleased });
    } catch (err) {
      // Revert on failure
      if (itemType === 'Exam') {
        setExams(prev => prev.map(e => e._id === item._id ? { ...e, released: item.released } : e));
      } else {
        setDocuments(prev => prev.map(d => d._id === item._id ? { ...d, released: item.released } : d));
      }
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const descendantIds = (folderId) => {
    const ids = new Set();
    const stack = [folderId];
    while (stack.length) {
      const currentId = stack.pop();
      folders.forEach(folder => {
        if ((folder.parent?._id ?? folder.parent)?.toString() === currentId) {
          ids.add(folder._id);
          stack.push(folder._id);
        }
      });
    }
    return ids;
  };

  const handleEditFolderInternal = (folder) => {
    setEditingFolder(folder);
    setEditFolderTitle(folder.title);
    setEditFolderDescription(folder.description || '');
    setEditFolderParentId((folder.parent?._id ?? folder.parent)?.toString() || '');
    setEditFolderReleased(!!folder.released);
    setEditFolderAllowedGroups(folder.allowedGroups || []);
    setEditFolderAllowedUsers(folder.allowedUsers || []);
    setEditFolderIsCollege(!!folder.isCollegeFolder);
    setEditFolderError('');
  };

  const handleSaveFolder = async () => {
    if (!editingFolder) return;
    try {
      await api.put(`/folders/${editingFolder._id}`, {
        title: editFolderTitle,
        description: editFolderDescription,
        parent: editFolderParentId || null,
        released: editFolderReleased,
        allowedGroups: editFolderAllowedGroups,
        allowedUsers: editFolderAllowedUsers,
        isCollegeFolder: editFolderIsCollege,
      });
      // Refresh folders
      const isStaff = ['super_user', 'college_admin', 'teacher'].includes(user.role);
      const folderEndpoint = isStaff ? '/folders/editfolders' : '/folders';
      const fRes = await api.get(folderEndpoint);
      setFolders(fRes.data);
      setEditingFolder(null);
    } catch (err) {
      setEditFolderError(err.response?.data?.error || 'Failed to update folder');
    }
  };

  const handleDeleteFolder = async (folder) => {
    const target = folder || editingFolder;
    if (!target) return;
    if (!window.confirm(`Delete folder "${target.title}" and all its contents?`)) return;
    try {
      await api.delete(`/folders/${target._id}`);
      if (selectedFolder?._id === target._id) setSelectedFolder(null);
      setEditingFolder(null);
      // Refresh folders
      const isStaff = ['super_user', 'college_admin', 'teacher'].includes(user.role);
      const folderEndpoint = isStaff ? '/folders/editfolders' : '/folders';
      const fRes = await api.get(folderEndpoint);
      setFolders(fRes.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete folder');
    }
  };

  const handleToggleFolderRelease = async (folder) => {
    const newReleased = !folder.released;
    setFolders(prev => prev.map(f => f._id === folder._id ? { ...f, released: newReleased } : f));
    try {
      await api.put(`/folders/${folder._id}`, {
        title: folder.title,
        parent: (folder.parent?._id ?? folder.parent) || null,
        released: newReleased,
      });
    } catch (err) {
      setFolders(prev => prev.map(f => f._id === folder._id ? { ...f, released: folder.released } : f));
      alert('Failed to update folder status');
    }
  };

  const handleCreateFolder = async (folderData) => {
    try {
      await api.post('/folders', folderData);
      const isStaff = ['super_user', 'college_admin', 'teacher'].includes(user.role);
      const folderEndpoint = isStaff ? '/folders/editfolders' : '/folders';
      const fRes = await api.get(folderEndpoint);
      setFolders(fRes.data);
      setShowCreateFolderModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create folder');
    }
  };

  const handleDragStart = (e, folder) => {
    setDraggedFolder(folder);
    e.dataTransfer.setData('folderId', folder._id);
  };

  const handleDragStartPaper = (e, paperType, paper) => {
    setDraggedPaper(paper);
    e.dataTransfer.setData('paperId', paper._id);
    e.dataTransfer.setData('paperType', paperType);
  };

  const handleDragOver = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    if ((draggedFolder && draggedFolder._id !== targetId) || (draggedPaper && targetId !== (draggedPaper.folder?._id ?? draggedPaper.folder))) {
      setDragOverId(targetId);
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDragOverItem = (e, item, itemType) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedFolder) {
      if (itemType === 'folder' && item._id !== draggedFolder._id) {
        const rect = e.currentTarget.getBoundingClientRect();
        let position = 'inside';

        if (viewMode === 'grid') {
          const x = e.clientX - rect.left;
          const w = rect.width;
          if (x < w * 0.1) {
            position = 'before';
          } else if (x > w * 0.9) {
            position = 'after';
          }
        } else {
          const y = e.clientY - rect.top;
          const h = rect.height;
          if (y < h * 0.1) {
            position = 'before';
          } else if (y > h * 0.9) {
            position = 'after';
          }
        }
        setDragOverTarget({ id: item._id, type: 'folder', position });
      }
    } else if (draggedPaper) {
      if (itemType === 'folder') {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const w = rect.width;
        const h = rect.height;
        const isInside = x >= w * 0.1 && x <= w * 0.9 && y >= h * 0.1 && y <= h * 0.9;
        if (isInside) {
          setDragOverTarget({ id: item._id, type: 'folder', position: 'inside' });
        } else {
          setDragOverTarget(null);
        }
      } else if (itemType === 'paper' && item._id !== draggedPaper._id) {
        const rect = e.currentTarget.getBoundingClientRect();
        let position = 'before';
        if (viewMode === 'grid') {
          const x = e.clientX - rect.left;
          if (x > rect.width / 2) {
            position = 'after';
          }
        } else {
          const y = e.clientY - rect.top;
          if (y > rect.height / 2) {
            position = 'after';
          }
        }
        setDragOverTarget({ id: item._id, type: 'paper', position });
      }
    }
  };

  const handleDragLeaveItem = () => {
    setDragOverTarget(null);
  };

  const getFolderDragStyle = (folderId, subfolders) => {
    if (!dragOverTarget || dragOverTarget.type !== 'folder' || !draggedFolder) return {};
    if (dragOverTarget.position === 'inside') {
      if (dragOverTarget.id === folderId) {
        return {
          border: '2px dashed var(--primary)',
          backgroundColor: 'rgba(79, 70, 229, 0.05)',
          transform: 'scale(1.02)',
          transition: 'all 0.2s ease'
        };
      }
      return {};
    }

    const draggedIdx = subfolders.findIndex(sf => sf._id === draggedFolder._id);
    const targetIdx = subfolders.findIndex(sf => sf._id === dragOverTarget.id);
    if (draggedIdx === -1 || targetIdx === -1) return {};

    const currentIdx = subfolders.findIndex(sf => sf._id === folderId);
    if (currentIdx === -1) return {};

    let gapIdx = targetIdx;
    if (dragOverTarget.position === 'after') {
      gapIdx = targetIdx + 1;
    }

    const isAdjacentBefore = currentIdx === gapIdx - 1;
    const isAdjacentAfter = currentIdx === gapIdx;

    let style = { transition: 'transform 0.2s ease, border 0.2s ease, box-shadow 0.2s ease' };

    if (isAdjacentBefore || isAdjacentAfter) {
      style.border = '2px solid var(--primary)';
      style.boxShadow = '0 0 8px rgba(79, 70, 229, 0.4)';
    }

    const shiftSize = viewMode === 'grid' ? '20px' : '12px';
    const transformProp = viewMode === 'grid' ? 'translateX' : 'translateY';

    if (draggedIdx < gapIdx) {
      if (currentIdx >= draggedIdx && currentIdx < gapIdx) {
        if (currentIdx !== draggedIdx) {
          style.transform = `${transformProp}(-${shiftSize})`;
        }
      }
    } else {
      if (currentIdx >= gapIdx && currentIdx <= draggedIdx) {
        if (currentIdx !== draggedIdx) {
          style.transform = `${transformProp}(${shiftSize})`;
        }
      }
    }

    return style;
  };

  const getPaperDragStyle = (paperId) => {
    if (!dragOverTarget || dragOverTarget.type !== 'paper' || !draggedPaper) return {};

    const draggedIdx = folderPapers.findIndex(p => p._id === draggedPaper._id);
    const targetIdx = folderPapers.findIndex(p => p._id === dragOverTarget.id);
    if (draggedIdx === -1 || targetIdx === -1) return {};

    const currentIdx = folderPapers.findIndex(p => p._id === paperId);
    if (currentIdx === -1) return {};

    let gapIdx = targetIdx;
    if (dragOverTarget.position === 'after') {
      gapIdx = targetIdx + 1;
    }

    const isAdjacentBefore = currentIdx === gapIdx - 1;
    const isAdjacentAfter = currentIdx === gapIdx;

    let style = { transition: 'transform 0.2s ease, border 0.2s ease, box-shadow 0.2s ease' };

    if (isAdjacentBefore || isAdjacentAfter) {
      style.border = '2px solid var(--primary)';
      style.boxShadow = '0 0 8px rgba(79, 70, 229, 0.4)';
    }

    const shiftSize = viewMode === 'grid' ? '24px' : '16px';
    const transformProp = viewMode === 'grid' ? 'translateX' : 'translateY';

    if (draggedIdx < gapIdx) {
      if (currentIdx >= draggedIdx && currentIdx < gapIdx) {
        if (currentIdx !== draggedIdx) {
          style.transform = `${transformProp}(-${shiftSize})`;
        }
      }
    } else {
      if (currentIdx >= gapIdx && currentIdx <= draggedIdx) {
        if (currentIdx !== draggedIdx) {
          style.transform = `${transformProp}(${shiftSize})`;
        }
      }
    }

    return style;
  };

  const handleDropItem = async (e, targetItem, targetType) => {
    e.preventDefault();
    e.stopPropagation();

    const folderId = e.dataTransfer.getData('folderId');
    const paperId = e.dataTransfer.getData('paperId');
    const paperType = e.dataTransfer.getData('paperType');

    const target = dragOverTarget;
    setDragOverTarget(null);
    setDraggedFolder(null);
    setDraggedPaper(null);

    if (!target) return;

    if (folderId && target.type === 'folder') {
      const folderToMove = folders.find(f => f._id === folderId);
      if (!folderToMove) return;

      if (target.position === 'inside') {
        if (folderId === target.id) return;
        if (!canUserManageFolder(user, folderId, folders)) {
          alert("You don't have permission to move this folder.");
          return;
        }
        if (!canUserCreateInFolder(user, target.id, folders)) {
          alert("You don't have permission to move folders into this destination.");
          return;
        }

        const isDescendant = (parentId, descendantId) => {
          const subfolders = folders.filter(f => (f.parent?._id ?? f.parent)?.toString() === parentId);
          if (subfolders.some(s => s._id === descendantId)) return true;
          return subfolders.some(s => isDescendant(s._id, descendantId));
        };
        if (isDescendant(folderId, target.id)) {
          alert("Cannot move a folder into its own descendant.");
          return;
        }

        try {
          await api.put(`/folders/${folderId}`, {
            ...folderToMove,
            parent: target.id,
            allowedGroups: folderToMove.allowedGroups?.map(g => g._id ?? g) || [],
            allowedUsers: folderToMove.allowedUsers?.map(u => u._id ?? u) || []
          });
          const isStaff = ['super_user', 'college_admin', 'teacher'].includes(user.role);
          const folderEndpoint = isStaff ? '/folders/editfolders' : '/folders';
          const fRes = await api.get(folderEndpoint);
          setFolders(fRes.data);
        } catch (err) {
          alert(err.response?.data?.error || 'Failed to move folder');
        }
      } else {
        if (!canUserManageFolder(user, folderId, folders)) {
          alert("You don't have permission to reorder folders.");
          return;
        }

        const parentId = selectedFolder?._id || null;
        const siblings = folders.filter(f => {
          const pId = f.parent?._id ?? f.parent;
          if (!parentId) return !pId;
          return pId?.toString() === parentId;
        }).sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title));

        const draggedIdx = siblings.findIndex(s => s._id === folderId);
        const targetIdx = siblings.findIndex(s => s._id === target.id);
        if (draggedIdx === -1 || targetIdx === -1) return;

        const updatedSiblings = [...siblings];
        const [movedFolder] = updatedSiblings.splice(draggedIdx, 1);
        let insertIdx = targetIdx;
        if (target.position === 'after') {
          insertIdx = draggedIdx < targetIdx ? targetIdx : targetIdx + 1;
        } else {
          insertIdx = draggedIdx < targetIdx ? targetIdx - 1 : targetIdx;
        }
        updatedSiblings.splice(insertIdx, 0, movedFolder);

        const reorderedFolders = folders.map(f => {
          const matchingSibling = updatedSiblings.find(s => s._id === f._id);
          if (matchingSibling) {
            const index = updatedSiblings.findIndex(s => s._id === f._id);
            return { ...f, order: index };
          }
          return f;
        });
        setFolders(reorderedFolders);

        try {
          await api.put('/folders/reorder', {
            folderIds: updatedSiblings.map(s => s._id)
          });
        } catch (err) {
          const isStaff = ['super_user', 'college_admin', 'teacher'].includes(user.role);
          const folderEndpoint = isStaff ? '/folders/editfolders' : '/folders';
          const fRes = await api.get(folderEndpoint);
          setFolders(fRes.data);
          alert(err.response?.data?.error || 'Failed to reorder folders on server');
        }
      }
    } else if (paperId) {
      if (target.type === 'folder' && target.position === 'inside') {
        const paper = paperType === 'Exam'
          ? exams.find(e => e._id === paperId)
          : documents.find(d => d._id === paperId);
        if (!paper) return;

        if (!canUserManageContent(user, paper)) {
          alert("You don't have permission to move this item.");
          return;
        }
        if (!canUserCreateInFolder(user, target.id, folders)) {
          alert("You don't have permission to move items into this folder.");
          return;
        }

        const endpoint = paperType === 'Exam' ? '/exams' : '/documents';
        try {
          await api.put(`${endpoint}/${paperId}`, { folder: target.id });
          if (paperType === 'Exam') {
            setExams(prev => prev.map(e => e._id === paperId ? { ...e, folder: target.id } : e));
          } else {
            setDocuments(prev => prev.map(d => d._id === paperId ? { ...d, folder: target.id } : d));
          }
        } catch (err) {
          alert(err.response?.data?.error || 'Failed to move item into folder');
        }
      } else if (target.type === 'paper') {
        const paper = paperType === 'Exam'
          ? exams.find(e => e._id === paperId)
          : documents.find(d => d._id === paperId);
        if (!paper) return;

        if (!canUserManageContent(user, paper)) {
          alert("You don't have permission to reorder items.");
          return;
        }

        const draggedIdx = folderPapers.findIndex(p => p._id === paperId);
        const targetIdx = folderPapers.findIndex(p => p._id === target.id);
        if (draggedIdx === -1 || targetIdx === -1) return;

        const updatedPapers = [...folderPapers];
        const [movedPaper] = updatedPapers.splice(draggedIdx, 1);
        let insertIdx = targetIdx;
        if (target.position === 'after') {
          insertIdx = draggedIdx < targetIdx ? targetIdx : targetIdx + 1;
        } else {
          insertIdx = draggedIdx < targetIdx ? targetIdx - 1 : targetIdx;
        }
        updatedPapers.splice(insertIdx, 0, movedPaper);

        const nextPapers = updatedPapers.map((p, idx) => ({ ...p, order: idx }));
        
        setExams(prev => prev.map(e => {
          const reordered = nextPapers.find(np => np._id === e._id && np.itemType === 'Exam');
          return reordered ? { ...e, order: reordered.order } : e;
        }));
        setDocuments(prev => prev.map(d => {
          const reordered = nextPapers.find(np => np._id === d._id && np.itemType === 'Document');
          return reordered ? { ...d, order: reordered.order } : d;
        }));

        try {
          const folderId = selectedFolder?._id || 'root';
          const endpoint = folderId === 'root'
            ? '/folders/reorder-papers/root'
            : `/folders/${folderId}/reorder-papers`;

          await api.put(endpoint, {
            papers: nextPapers.map(p => ({ id: p._id, type: p.itemType }))
          });
        } catch (err) {
          const [dRes, eRes] = await Promise.all([
            api.get('/documents'),
            api.get('/exams')
          ]);
          setDocuments(dRes.data);
          setExams(eRes.data);
          alert(err.response?.data?.error || 'Failed to reorder papers on server');
        }
      }
    }
  };

  const handleDropFolder = async (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    const folderId = e.dataTransfer.getData('folderId');
    const paperId = e.dataTransfer.getData('paperId');
    const paperType = e.dataTransfer.getData('paperType');
    
    setDragOverId(null);
    setDraggedFolder(null);
    setDraggedPaper(null);

    if (folderId) {
      if (folderId === targetId) return;

      // Check permissions
      if (!canUserManageFolder(user, folderId, folders)) {
        alert("You don't have permission to move this folder.");
        return;
      }
      if (targetId && !canUserCreateInFolder(user, targetId, folders)) {
        alert("You don't have permission to move folders into this destination.");
        return;
      }
      if (!targetId && user?.role !== 'super_user') {
        alert("Only super users can move folders to the root.");
        return;
      }

      try {
        const folderToMove = folders.find(f => f._id === folderId);
        if (!folderToMove) return;

        // Circularity check
        const isDescendant = (parentId, descendantId) => {
          const subfolders = folders.filter(f => (f.parent?._id ?? f.parent)?.toString() === parentId);
          if (subfolders.some(s => s._id === descendantId)) return true;
          return subfolders.some(s => isDescendant(s._id, descendantId));
        };

        if (targetId && isDescendant(folderId, targetId)) {
          alert("Cannot move a folder into its own descendant.");
          return;
        }

        await api.put(`/folders/${folderId}`, { 
          ...folderToMove, 
          parent: targetId || null,
          // Ensure we preserve groups/users
          allowedGroups: folderToMove.allowedGroups?.map(g => g._id ?? g) || [],
          allowedUsers: folderToMove.allowedUsers?.map(u => u._id ?? u) || []
        });
        
        const isStaff = ['super_user', 'college_admin', 'teacher'].includes(user.role);
        const folderEndpoint = isStaff ? '/folders/editfolders' : '/folders';
        const fRes = await api.get(folderEndpoint);
        setFolders(fRes.data);
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to move folder');
      }
    } else if (paperId) {
      const paper = paperType === 'Exam' 
        ? exams.find(e => e._id === paperId) 
        : documents.find(d => d._id === paperId);
      
      if (!paper) return;
      const currentFolderId = (paper.folder?._id ?? paper.folder)?.toString() || '';
      const targetFolderId = targetId || '';
      if (currentFolderId === targetFolderId) return;

      if (!canUserManageContent(user, paper)) {
        alert("You don't have permission to move this item.");
        return;
      }

      if (targetId && !canUserCreateInFolder(user, targetId, folders)) {
        alert("You don't have permission to move items into this folder.");
        return;
      }

      if (!targetId && user?.role !== 'super_user') {
        alert("Only super users can move items to the root.");
        return;
      }

      const endpoint = paperType === 'Exam' ? '/exams' : '/documents';
      try {
        await api.put(`${endpoint}/${paperId}`, { folder: targetId || '' });

        // Update local state
        if (paperType === 'Exam') {
          setExams(prev => prev.map(e => e._id === paperId ? { ...e, folder: targetId || null } : e));
        } else {
          setDocuments(prev => prev.map(d => d._id === paperId ? { ...d, folder: targetId || null } : d));
        }
      } catch (err) {
        alert(err.response?.data?.error || `Failed to move ${paperType.toLowerCase()}`);
      }
    }
  };

  const folderCounts = useMemo(() => {
    const counts = new Map();
    
    // Helper to increment count in map
    const inc = (id) => {
      if (!id) return;
      const sId = id.toString();
      counts.set(sId, (counts.get(sId) || 0) + 1);
    };

    folders.forEach(f => inc(f.parent?._id ?? f.parent));
    documents.forEach(d => inc(d.folder?._id ?? d.folder));
    exams.forEach(e => inc(e.folder?._id ?? e.folder));
    
    return counts;
  }, [folders, documents, exams]);

  const getItemCount = useCallback((folderId) => {
    return folderCounts.get(folderId?.toString()) || 0;
  }, [folderCounts]);

  const selectedPath = (() => {
    const path = [];
    let current = selectedFolder;
    while (current) {
      path.unshift(current);
      const parentId = current.parent?._id ?? current.parent;
      current = parentId ? folders.find(f => f._id === parentId.toString()) : null;
    }
    return path;
  })();

  const getVisibleFiles = (item) => {
    if (item.files && item.files.length > 0) {
      return item.files.filter(f => f.visibility === 'public' || user?.role === 'super_user' || item.createdBy === user?._id || (item.authorizedEditors || []).includes(user?._id));
    }
    return item.fileUrl ? [{ url: item.fileUrl, name: 'Document', visibility: 'public' }] : [];
  };

  const tree = buildTree(folders);
  const folderDocs = selectedFolder ? documents.filter(d => (d.folder?._id ?? d.folder)?.toString() === selectedFolder._id) : documents.filter(d => !d.folder);
  const folderExams = selectedFolder ? exams.filter(e => (e.folder?._id ?? e.folder)?.toString() === selectedFolder._id) : exams.filter(e => !e.folder);

  const subfolders = useMemo(() => {
    return folders.filter(f => {
      const parentId = f.parent?._id ?? f.parent;
      if (!selectedFolder) return !parentId;
      return parentId?.toString() === selectedFolder._id;
    }).sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title));
  }, [folders, selectedFolder]);

  const folderPapers = useMemo(() => {
    const docs = folderDocs.map(d => ({ ...d, itemType: 'Document' }));
    const exms = folderExams.map(e => ({ ...e, itemType: 'Exam' }));
    return [...docs, ...exms].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title));
  }, [folderDocs, folderExams]);

  const hasContent = folderPapers.length > 0;

  // ── Search Results Logic ──
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return {
      folders: folders.filter(f => f.title.toLowerCase().includes(q)),
      documents: documents.filter(d => d.title.toLowerCase().includes(q)),
      exams: exams.filter(e => e.title.toLowerCase().includes(q))
    };
  }, [searchQuery, folders, documents, exams]);

  const isSearching = !!searchResults;

  // SEO metadata
  const seoTitle = searchQuery 
    ? `Search: ${searchQuery}` 
    : (selectedFolder ? selectedFolder.title : 'Browse Library');
  
  const seoDescription = selectedFolder?.description 
    ? selectedFolder.description 
    : `Browse documents and exams in ${seoTitle}. Prepare for your next big test with Study Centro.`;

  return (
    <>
    <div className="browse-root">
      <SEO title={seoTitle} description={seoDescription} />
      <div className="browse-body" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ── Sidebar ── */}
        <aside className="browse-sidebar" style={{ width: sidebarCollapsed ? '60px' : '260px', transition: 'width 0.3s ease', overflow: 'hidden' }}>
          <div className="sidebar-header" style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between', padding: sidebarCollapsed ? '16px 0' : '16px 20px' }}>
            <button className="premium-icon-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
              <i className={`fas fa-${sidebarCollapsed ? 'indent' : 'outdent'}`} />
            </button>
          </div>
          {!sidebarCollapsed && (
            <div style={{ overflowY: 'auto', flex: 1, padding: '12px 0' }}>
              {tree.map(node => (
                <SidebarFolder
                  key={node._id}
                  node={node}
                  selectedId={selectedFolder?._id}
                  onSelect={handleSelectFolder}
                  expanded={expanded}
                  onToggle={handleToggle}
                  getItemCount={getItemCount}
                  draggedFolder={draggedFolder}
                  dragOverId={dragOverId}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDropFolder}
                  user={user}
                  folders={folders}
                />
              ))}
            </div>
          )}
        </aside>

        {/* ── Mobile Folder Drawer ── */}
        {mobileDrawerOpen && (
          <>
            <div className="browse-sidebar-overlay" onClick={() => setMobileDrawerOpen(false)} />
            <div className="browse-sidebar-drawer">
              <div className="browse-sidebar-drawer-header">
                <button
                  className="browse-sidebar-drawer-close"
                  onClick={() => setMobileDrawerOpen(false)}
                  title="Close"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
              <div className="browse-sidebar-drawer-body">
                {tree.map(node => (
                  <SidebarFolder
                    key={node._id}
                    node={node}
                    selectedId={selectedFolder?._id}
                    onSelect={handleSelectFolder}
                    expanded={expanded}
                    onToggle={handleToggle}
                    getItemCount={getItemCount}
                    draggedFolder={null}
                    dragOverId={null}
                    onDragStart={() => {}}
                    onDragOver={() => {}}
                    onDragLeave={() => {}}
                    onDrop={() => {}}
                    user={user}
                    folders={folders}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Mobile FAB to open drawer ── */}
        <button
          className="browse-mobile-fab"
          onClick={() => setMobileDrawerOpen(true)}
          title="Browse folders"
          aria-label="Open folder navigation"
        >
          <i className="fas fa-folder-open" />
        </button>

        {/* ── Main Content Area (Fixed Action Bar + Scrollable Content) ── */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {/* Fixed Action Bar */}
          <div className={`premium-action-bar${!isHeaderVisible ? ' header-hidden' : ''}`} style={{ margin: '0', width: '100%', borderRadius: '0', borderLeft: 'none', borderRight: 'none', borderTop: 'none', padding: '8px 32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', overflow: 'hidden' }}>
              <button className="link-button" onClick={() => handleSelectFolder(null)} style={{ fontSize: '0.85rem', padding: '4px 8px' }}>
                <i className="fas fa-home" style={{ marginRight: '4px' }} /> Home
              </button>
              {selectedPath.map(node => (
                <div key={node._id} className="breadcrumb-item" style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ opacity: 0.3, margin: '0 2px' }}>/</span>
                  <button className="link-button" onClick={() => handleSelectFolder(node)} style={{ fontSize: '0.85rem', padding: '4px 8px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <MathText key={node.title} text={node.title} />
                  </button>
                </div>
              ))}
            </div>

            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', maxWidth: '400px', margin: '0 20px' }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <i className="fas fa-search" style={{ 
                  position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', 
                  color: 'var(--text-muted)', fontSize: '0.8rem', pointerEvents: 'none' 
                }} />
                <input
                  type="text"
                  placeholder="Search library..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ 
                    width: '100%', padding: '6px 12px 6px 36px', fontSize: '0.85rem', 
                    background: 'var(--bg-main)', border: '1px solid var(--border-light)', 
                    borderRadius: '20px', height: '34px'
                  }}
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    style={{ 
                      position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
                      padding: '4px', display: 'flex', alignItems: 'center'
                    }}
                  >
                    <i className="fas fa-times-circle" />
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ position: 'relative' }}>
                <button 
                  className="secondary small"
                  onClick={(e) => { e.stopPropagation(); setViewSelectorOpen(prev => !prev); }}
                  style={{ padding: '6px 10px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                  title={`Current view: ${viewMode}. Click to change.`}
                >
                  {viewMode === 'grid' && <><i className="fas fa-th-large" /> Grid</>}
                  {viewMode === 'list' && <><i className="fas fa-list" /> List</>}
                  {viewMode === 'details' && <><i className="fas fa-th-list" /> Details</>}
                  <i className="fas fa-chevron-down" style={{ fontSize: '0.65rem', marginLeft: '4px', opacity: 0.7 }} />
                </button>
                {viewSelectorOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    boxShadow: 'var(--shadow-md)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    padding: '4px',
                    zIndex: 100
                  }}>
                    <button 
                      className="secondary small" 
                      onClick={() => {
                        setViewMode('grid');
                        localStorage.setItem('studymate_view_mode', 'grid');
                        setViewSelectorOpen(false);
                      }} 
                      style={{ 
                        padding: '6px 12px', 
                        fontSize: '0.8rem', 
                        background: viewMode === 'grid' ? 'var(--primary)' : 'transparent', 
                        color: viewMode === 'grid' ? 'white' : 'var(--text-main)',
                        border: 'none',
                        justifyContent: 'flex-start',
                        width: '100%',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <i className="fas fa-th-large" style={{ marginRight: '8px', width: '14px' }} /> Grid
                    </button>
                    <button 
                      className="secondary small" 
                      onClick={() => {
                        setViewMode('list');
                        localStorage.setItem('studymate_view_mode', 'list');
                        setViewSelectorOpen(false);
                      }} 
                      style={{ 
                        padding: '6px 12px', 
                        fontSize: '0.8rem', 
                        background: viewMode === 'list' ? 'var(--primary)' : 'transparent', 
                        color: viewMode === 'list' ? 'white' : 'var(--text-main)',
                        border: 'none',
                        justifyContent: 'flex-start',
                        width: '100%',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <i className="fas fa-list" style={{ marginRight: '8px', width: '14px' }} /> List
                    </button>
                    <button 
                      className="secondary small" 
                      onClick={() => {
                        setViewMode('details');
                        localStorage.setItem('studymate_view_mode', 'details');
                        setViewSelectorOpen(false);
                      }} 
                      style={{ 
                        padding: '6px 12px', 
                        fontSize: '0.8rem', 
                        background: viewMode === 'details' ? 'var(--primary)' : 'transparent', 
                        color: viewMode === 'details' ? 'white' : 'var(--text-main)',
                        border: 'none',
                        justifyContent: 'flex-start',
                        width: '100%',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <i className="fas fa-th-list" style={{ marginRight: '8px', width: '14px' }} /> Details
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '6px' }}>
              {canUserCreateInFolder(user, selectedFolder?._id, folders) && (
                <>
                  <button className="secondary small" onClick={() => setShowCreateFolderModal(true)} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
                    <i className="fas fa-folder-plus" style={{ marginRight: '4px' }} /> Folder
                  </button>
                  {user?.role === 'super_user' && (
                    <button
                      className="secondary small"
                      onClick={() => setShowBulkUpload(true)}
                      style={{ padding: '4px 10px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                      title="Bulk upload multiple files — file name becomes document title"
                    >
                      <i className="fas fa-upload" style={{ marginRight: '4px' }} /> Bulk Upload
                    </button>
                  )}
                  <button className="primary small" onClick={() => onCreateContent(selectedFolder?._id)} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
                    <i className="fas fa-plus" style={{ marginRight: '4px' }} /> Create
                  </button>
                </>
              )}
              {selectedFolder && canUserManageFolder(user, selectedFolder, folders) && (
                <button className="secondary small" onClick={() => handleEditFolderInternal(selectedFolder)} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
                  <i className="fas fa-cog" style={{ marginRight: '4px' }} /> Manage
                </button>
              )}
            </div>
          </div>
          </div>
          
          {/* Scrollable Content Container */}
          <main 
            className={`browse-content${(dragOverId === 'root' || (selectedFolder && dragOverId === selectedFolder._id)) ? ' drag-over' : ''}`}
            data-drag-text={selectedFolder ? `Move into ${selectedFolder.title}` : 'Move to Root'}
            onDragOver={(e) => { 
              e.preventDefault(); 
              if (draggedFolder) {
                const targetId = selectedFolder?._id || 'root';
                // Only allow dropping to root if super_user
                if (targetId === 'root' && user?.role !== 'super_user') return;
                setDragOverId(targetId);
              }
            }} 
            onDragLeave={() => setDragOverId(null)} 
            onDrop={(e) => handleDropFolder(e, selectedFolder?._id || null)}
            style={{ overflowY: 'auto', flex: 1 }}
            id="browse-content-area"
          >
            {error && <div className="error-message" style={{ margin: '20px 32px 0' }}>{error}</div>}

            {showCreateFolderModal && (
              <CreateFolderModal
                parentId={selectedFolder?._id || ''}
                folders={folders}
                folderMap={new Map(folders.map(f => [f._id, f]))}
                onClose={() => setShowCreateFolderModal(false)}
                onSave={handleCreateFolder}
              />
            )}

            {editingFolder && (
              <FolderDetailModal
                folder={editingFolder}
                path={[]} 
                parentOptions={folders.filter(f => f._id !== editingFolder._id && !descendantIds(editingFolder._id).has(f._id))}
                editTitle={editFolderTitle}
                editDescription={editFolderDescription}
                editParentId={editFolderParentId}
                editReleased={editFolderReleased}
                editAllowedGroups={editFolderAllowedGroups}
                editAllowedUsers={editFolderAllowedUsers}
                editIsCollege={editFolderIsCollege}
                users={users}
                userGroups={userGroups}
                user={user}
                onTitleChange={(e) => setEditFolderTitle(e.target.value)}
                onDescriptionChange={(e) => setEditFolderDescription(e.target.value)}
                onParentChange={(e) => setEditFolderParentId(e.target.value)}
                onReleasedChange={(e) => setEditFolderReleased(e.target.checked)}
                onAllowedGroupsChange={setEditFolderAllowedGroups}
                onAllowedUsersChange={setEditFolderAllowedUsers}
                onTypeToggle={() => setEditFolderIsCollege(p => !p)}
                onSave={handleSaveFolder}
                onDelete={() => handleDeleteFolder(editingFolder)}
                onClose={() => setEditingFolder(null)}
                error={editFolderError}
              />
            )}

            {deleteConfirm && (
              <Modal isOpen={true} onClose={() => setDeleteConfirm(null)} title={`Delete ${deleteConfirm.itemType}?`} style={{ maxWidth: '420px', textAlign: 'center' }}>
                <p style={{ margin: '16px 0', color: 'var(--text-muted)' }}>Are you sure you want to delete this {deleteConfirm.itemType.toLowerCase()} "{deleteConfirm.item.title || ''}"? This action cannot be undone.</p>
                <div className="modal-actions" style={{ justifyContent: 'center' }}>
                  <button type="button" className="secondary" onClick={() => setDeleteConfirm(null)}>No, Keep It</button>
                  <button type="button" onClick={performDelete} style={{ background: 'var(--danger)' }}>Yes, Delete</button>
                </div>
              </Modal>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '100px', opacity: 0.5 }}>
                <i className="fas fa-circle-notch fa-spin fa-2x" />
              </div>
            ) : (
            <div style={{ padding: '24px 32px 32px' }}>
              {isSearching ? (
                /* ── Search Results View ── */
                <div>
                  <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h2 style={{ margin: 0, fontSize: '1.4rem' }}>Search Results</h2>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                      Found {searchResults.folders.length} folders, {searchResults.documents.length} docs, {searchResults.exams.length} exams
                    </span>
                  </div>

                  {searchResults.folders.length > 0 && (
                    <div style={{ marginBottom: '40px' }}>
                      <div style={
                        viewMode === 'grid' 
                          ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '16px' } 
                          : viewMode === 'list'
                          ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px' }
                          : { display: 'flex', flexDirection: 'column', gap: '8px' }
                      }>
                        {searchResults.folders.map(f => {
                          if (viewMode === 'list') {
                            return (
                              <div 
                                key={f._id} 
                                className="premium-card" 
                                onClick={() => { handleSelectFolder(f); setSearchQuery(''); }} 
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '6px 10px', gap: '8px', flexWrap: 'nowrap', maxWidth: 'none', background: 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border-light)' }}
                                title={f.title}
                              >
                                <i className="fas fa-folder" style={{ opacity: 0.7, color: 'var(--primary)', flexShrink: 0, fontSize: '0.95rem' }} />
                                <span style={{ display: 'block', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, fontWeight: '500' }}>
                                  <MathText key={f.title} text={f.title} />
                                </span>
                              </div>
                            );
                          }
                          return (
                            <div 
                              key={f._id} 
                              className="premium-card" 
                              onClick={() => { handleSelectFolder(f); setSearchQuery(''); }} 
                              style={viewMode === 'details' ? { cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', gap: '16px', flexWrap: 'nowrap', maxWidth: 'none' } : { cursor: 'pointer', textAlign: 'center' }}
                            >
                              <div style={viewMode === 'details' ? { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' } : { position: 'relative', marginBottom: '12px' }}>
                                {viewMode === 'details' && <i className="fas fa-folder" style={{ opacity: 0.7, color: 'var(--primary)', marginRight: '4px' }} />}
                                <span style={{ display: 'block', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: viewMode === 'details' ? 'nowrap' : 'normal', fontWeight: '500' }}><MathText key={f.title} text={f.title} /></span>
                              </div>
                              <div style={viewMode === 'details' ? { display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 } : { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', marginTop: '4px' }}>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{getItemCount(f._id)} items</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div style={
                    viewMode === 'grid' 
                      ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '24px' } 
                      : viewMode === 'list'
                      ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '6px' }
                      : { display: 'flex', flexDirection: 'column', gap: '8px' }
                  }>
                    {searchResults.exams.map(exam => {
                      const isDraggable = canUserManageContent(user, exam);
                      if (viewMode === 'list') {
                        return (
                          <div 
                            key={exam._id} 
                            className="premium-card" 
                            onClick={() => onTakeTest(exam._id)}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '6px 10px', gap: '8px', flexWrap: 'nowrap', maxWidth: 'none', background: 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border-light)' }}
                            title={`Take Test: ${exam.title}`}
                            draggable={isDraggable}
                            onDragStart={(e) => handleDragStartPaper(e, 'Exam', exam)}
                            onDragEnd={() => setDraggedPaper(null)}
                          >
                            <i className="fas fa-file-signature" style={{ opacity: 0.7, color: 'var(--primary)', flexShrink: 0, fontSize: '0.95rem' }} />
                            <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, fontWeight: '500' }}>
                              <MathText key={exam.title} text={exam.title} />
                              {['super_user', 'college_admin', 'teacher'].includes(user?.role) && (
                                <span style={{ marginLeft: '8px', fontSize: '0.75rem', backgroundColor: '#6c757d', color: '#fff', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>{exam.views || 0}</span>
                              )}
                            </span>
                          </div>
                        );
                      }
                      return (
                        <div 
                          key={exam._id} 
                          className="premium-card" 
                          style={viewMode === 'details' ? { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', gap: '16px', flexWrap: 'nowrap', maxWidth: 'none' } : {}}
                          draggable={isDraggable}
                          onDragStart={(e) => handleDragStartPaper(e, 'Exam', exam)}
                          onDragEnd={() => setDraggedPaper(null)}
                        >
                          <div style={viewMode === 'details' ? { flex: 1, minWidth: 0 } : {}}>
                            <div style={{ fontSize: viewMode === 'details' ? '0.95rem' : '1.05rem', margin: viewMode === 'details' ? '0' : '0 0 8px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: viewMode === 'details' ? 'nowrap' : 'normal', fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                              <MathText key={exam.title} text={exam.title} />
                              {['super_user', 'college_admin', 'teacher'].includes(user?.role) && (
                                <span style={{ marginLeft: '8px', fontSize: '0.75rem', backgroundColor: '#6c757d', color: '#fff', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>{exam.views || 0}</span>
                              )}
                            </div>
                          </div>
                          <div style={viewMode === 'details' ? { display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 } : {}}>
                            {viewMode === 'details' && (
                              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                <p style={{ margin: 0 }}><i className="fas fa-question-circle" /> {exam.questions.length} Questions</p>
                              </div>
                            )}
                            <button className={viewMode === 'details' ? "secondary submit-btn-small" : "secondary"} style={viewMode === 'details' ? { margin: 0, padding: '6px 12px', flexShrink: 0, background: 'transparent', color: 'var(--primary)', borderColor: 'var(--primary)' } : { marginTop: '20px', background: 'transparent', color: 'var(--primary)', borderColor: 'var(--primary)' }} onClick={() => onTakeTest(exam._id)}>
                              {exam.preferPdf ? (
                                <><i className="fas fa-file-pdf" style={{ marginRight: '8px' }} /> View PDF</>
                              ) : (
                                'Take Test'
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {searchResults.documents.map(doc => {
                      const visibleFiles = getVisibleFiles(doc);
                      const isDraggable = canUserManageContent(user, doc);
                      if (viewMode === 'list') {
                        return (
                          <div 
                            key={doc._id} 
                            className="premium-card" 
                            onClick={() => visibleFiles.length > 0 && onViewDocument(doc._id)}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '6px 10px', gap: '8px', flexWrap: 'nowrap', maxWidth: 'none', background: 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border-light)' }}
                            title={`Open Document: ${doc.title}`}
                            draggable={isDraggable}
                            onDragStart={(e) => handleDragStartPaper(e, 'Document', doc)}
                            onDragEnd={() => setDraggedPaper(null)}
                          >
                            <i className="fas fa-file-alt" style={{ opacity: 0.7, color: 'var(--blue)', flexShrink: 0, fontSize: '0.95rem' }} />
                            <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, fontWeight: '500' }}>
                              <MathText key={doc.title} text={doc.title} />
                              {['super_user', 'college_admin', 'teacher'].includes(user?.role) && (
                                <span style={{ marginLeft: '8px', fontSize: '0.75rem', backgroundColor: '#6c757d', color: '#fff', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>{doc.views || 0}</span>
                              )}
                            </span>
                          </div>
                        );
                      }
                      return (
                        <div 
                          key={doc._id} 
                          className="premium-card" 
                          style={viewMode === 'details' ? { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', gap: '16px', flexWrap: 'nowrap', maxWidth: 'none' } : {}}
                          draggable={isDraggable}
                          onDragStart={(e) => handleDragStartPaper(e, 'Document', doc)}
                          onDragEnd={() => setDraggedPaper(null)}
                        >
                          <div style={viewMode === 'details' ? { flex: 1, minWidth: 0 } : {}}>
                            {viewMode === 'grid' && <div className="card-type-badge card-type-doc">Document</div>}
                            <div style={{ fontSize: viewMode === 'details' ? '0.95rem' : '1.05rem', margin: viewMode === 'details' ? '0' : '0 0 8px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: viewMode === 'details' ? 'nowrap' : 'normal', fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                              <MathText key={doc.title} text={doc.title} />
                              {['super_user', 'college_admin', 'teacher'].includes(user?.role) && (
                                <span style={{ marginLeft: '8px', fontSize: '0.75rem', backgroundColor: '#6c757d', color: '#fff', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>{doc.views || 0}</span>
                              )}
                            </div>
                          </div>
                          <div style={viewMode === 'details' ? { display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 } : {}}>
                            {viewMode === 'details' && (
                              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', display: 'flex', gap: '12px', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                {doc.college && (
                                  <span style={{ fontSize: '0.6rem', background: 'rgba(79, 70, 229, 0.1)', color: 'var(--primary)', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                                    COLLEGE
                                  </span>
                                )}
                                <p style={{ margin: 0 }}><i className="fas fa-file-alt" /> {doc.type}</p>
                              </div>
                            )}
                            {visibleFiles.length > 0 && (
                              <a href={`/document/${doc._id}`} onClick={(e) => { e.preventDefault(); onViewDocument(doc._id); }} className="link-button" style={viewMode === 'details' ? { margin: 0, padding: '6px 12px', fontWeight: '600', flexShrink: 0 } : { marginTop: '20px', textAlign: 'center', fontWeight: '600', display: 'block' }}>
                                {visibleFiles.length > 1 ? 'View Docs' : 'View Document'}
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {searchResults.folders.length === 0 && searchResults.documents.length === 0 && searchResults.exams.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '100px', opacity: 0.5 }}>
                      <i className="fas fa-search fa-3x" style={{ marginBottom: '20px' }} />
                      <p>No results found for "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              ) : (
                /* ── Normal Folder View ── */
                <>
                  {/* ── Subfolders Grid/List ── */}

                  {subfolders.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <div style={
                          viewMode === 'grid' 
                            ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '16px' } 
                            : viewMode === 'list'
                            ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px' }
                            : { display: 'flex', flexDirection: 'column', gap: '8px' }
                        }>
                          {subfolders.map(f => {
                            const isDraggable = canUserManageFolder(user, f, folders);
                            const dragStyle = getFolderDragStyle(f._id, subfolders);
                            const wrapperStyle = viewMode === 'details' 
                              ? { width: '100%' } 
                              : viewMode === 'list'
                              ? { width: '100%', maxWidth: 'none' }
                              : {};

                            if (viewMode === 'list') {
                              return (
                                <div
                                  key={f._id}
                                  className="drag-wrapper"
                                  style={wrapperStyle}
                                  onDragOver={(e) => handleDragOverItem(e, f, 'folder')}
                                  onDragLeave={handleDragLeaveItem}
                                  onDrop={(e) => handleDropItem(e, f, 'folder')}
                                >
                                  <div 
                                    className="premium-card" 
                                    onClick={() => handleSelectFolder(f)} 
                                    style={{ 
                                      cursor: 'pointer', 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      padding: '6px 10px', 
                                      gap: '8px', 
                                      flexWrap: 'nowrap', 
                                      maxWidth: 'none', 
                                      background: 'var(--bg-card)', 
                                      borderRadius: '6px', 
                                      border: '1px solid var(--border-light)',
                                      ...dragStyle 
                                    }}
                                    draggable={isDraggable}
                                    onDragStart={(e) => handleDragStart(e, f)}
                                    onDragEnd={handleDragLeaveItem}
                                    title={f.title}
                                  >
                                    <i className="fas fa-folder" style={{ opacity: 0.7, color: 'var(--primary)', flexShrink: 0, fontSize: '0.95rem' }} />
                                    <span style={{ display: 'block', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, fontWeight: '500' }}>
                                      <MathText key={f.title} text={f.title} />
                                    </span>
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={f._id}
                                className="drag-wrapper"
                                style={wrapperStyle}
                                onDragOver={(e) => handleDragOverItem(e, f, 'folder')}
                                onDragLeave={handleDragLeaveItem}
                                onDrop={(e) => handleDropItem(e, f, 'folder')}
                              >
                                <div 
                                  className="premium-card" 
                                  onClick={() => handleSelectFolder(f)} 
                                  style={{
                                    ...(viewMode === 'details' ? { cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', gap: '16px', flexWrap: 'nowrap', maxWidth: 'none' } : { cursor: 'pointer', textAlign: 'center' }),
                                    ...dragStyle
                                  }}
                                  draggable={isDraggable}
                                  onDragStart={(e) => handleDragStart(e, f)}
                                  onDragEnd={handleDragLeaveItem}
                                >
                                  <div style={viewMode === 'details' ? { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' } : { position: 'relative', marginBottom: '12px' }}>
                                    {viewMode === 'details' && <i className="fas fa-folder" style={{ opacity: 0.7, color: 'var(--primary)', marginRight: '4px' }} />}
                                    <span style={{ display: 'block', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: viewMode === 'details' ? 'nowrap' : 'normal', fontWeight: '500' }}><MathText key={f.title} text={f.title} /></span>
                                  </div>
                                  <div style={viewMode === 'details' ? { display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 } : { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', marginTop: '4px' }}>
                                    {viewMode === 'details' && (
                                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                        <span>{getItemCount(f._id)} items</span>
                                        {f.isCollegeFolder && (
                                          <span style={{ fontSize: '0.65rem', color: 'var(--primary)', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>COLLEGE</span>
                                        )}
                                      </div>
                                    )}
                                    {viewMode === 'grid' && (
                                      <>
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{getItemCount(f._id)} items</span>
                                        {f.isCollegeFolder && (
                                          <span style={{ fontSize: '0.65rem', color: 'var(--primary)', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>COLLEGE</span>
                                        )}
                                      </>
                                    )}
                                    {canUserManageFolder(user, f, folders) && (
                                      <div className={viewMode === 'details' ? "" : "management-overlay"} style={viewMode === 'details' ? { display: 'flex', gap: '4px' } : {}}>
                                        <button className="premium-icon-btn" style={viewMode === 'details' ? { padding: '4px', fontSize: '0.8rem' } : {}} onClick={(e) => { e.stopPropagation(); handleEditFolderInternal(f); }} title="Edit Folder Settings">
                                          <i className="fas fa-pencil-alt" />
                                        </button>
                                        <button className="premium-icon-btn" style={viewMode === 'details' ? { padding: '4px', fontSize: '0.8rem' } : {}} onClick={(e) => { e.stopPropagation(); handleToggleFolderRelease(f); }} title={f.released ? "Hide from Users" : "Release to Users"}>
                                          <i className={`fas fa-${f.released ? 'eye' : 'eye-slash'}`} />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}


                  {/* ── Documents & Exams ── */}
                  <div style={
                    viewMode === 'grid' 
                      ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '24px' } 
                      : viewMode === 'list'
                      ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '6px' }
                      : { display: 'flex', flexDirection: 'column', gap: '8px' }
                  }>
                    {folderPapers.map(paper => {
                      const isDraggable = canUserManageContent(user, paper);
                      const dragStyle = getPaperDragStyle(paper._id);
                      const wrapperStyle = viewMode === 'details' 
                        ? { width: '100%' } 
                        : viewMode === 'list'
                        ? { width: '100%', maxWidth: 'none' }
                        : {};

                      if (paper.itemType === 'Exam') {
                        if (viewMode === 'list') {
                          return (
                            <div
                              key={paper._id}
                              className="drag-wrapper"
                              style={wrapperStyle}
                              onDragOver={(e) => handleDragOverItem(e, paper, 'paper')}
                              onDragLeave={handleDragLeaveItem}
                              onDrop={(e) => handleDropItem(e, paper, 'paper')}
                            >
                              <div 
                                className="premium-card" 
                                onClick={() => onTakeTest(paper._id)}
                                style={{ 
                                  cursor: 'pointer', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  padding: '6px 10px', 
                                  gap: '8px', 
                                  flexWrap: 'nowrap', 
                                  maxWidth: 'none', 
                                  background: 'var(--bg-card)', 
                                  borderRadius: '6px', 
                                  border: '1px solid var(--border-light)',
                                  ...dragStyle 
                                }}
                                title={`Take Test: ${paper.title}`}
                                draggable={isDraggable}
                                onDragStart={(e) => handleDragStartPaper(e, 'Exam', paper)}
                                onDragEnd={handleDragLeaveItem}
                              >
                                <i className="fas fa-file-signature" style={{ opacity: 0.7, color: 'var(--primary)', flexShrink: 0, fontSize: '0.95rem' }} />
                                <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, fontWeight: '500' }}>
                                  <MathText key={paper.title} text={paper.title} />
                                  {['super_user', 'college_admin', 'teacher'].includes(user?.role) && (
                                    <span style={{ marginLeft: '8px', fontSize: '0.75rem', backgroundColor: '#6c757d', color: '#fff', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>{paper.views || 0}</span>
                                  )}
                                </span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={paper._id}
                            className="drag-wrapper"
                            style={wrapperStyle}
                            onDragOver={(e) => handleDragOverItem(e, paper, 'paper')}
                            onDragLeave={handleDragLeaveItem}
                            onDrop={(e) => handleDropItem(e, paper, 'paper')}
                          >
                            <div 
                              className="premium-card" 
                              style={{
                                ...(viewMode === 'details' ? { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', gap: '16px', flexWrap: 'nowrap', maxWidth: 'none' } : {}),
                                ...dragStyle
                              }}
                              draggable={isDraggable}
                              onDragStart={(e) => handleDragStartPaper(e, 'Exam', paper)}
                              onDragEnd={handleDragLeaveItem}
                            >
                              <div style={viewMode === 'details' ? { flex: 1, minWidth: 0 } : {}}>
                                <div style={{ fontSize: viewMode === 'details' ? '0.95rem' : '1.05rem', margin: viewMode === 'details' ? '0' : '0 0 8px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: viewMode === 'details' ? 'nowrap' : 'normal', fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                                  <MathText key={paper.title} text={paper.title} />
                                  {['super_user', 'college_admin', 'teacher'].includes(user?.role) && (
                                    <span style={{ marginLeft: '8px', fontSize: '0.75rem', backgroundColor: '#6c757d', color: '#fff', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>{paper.views || 0}</span>
                                  )}
                                </div>
                              </div>
                              <div style={viewMode === 'details' ? { display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 } : {}}>
                                {viewMode === 'details' && (
                                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', display: 'flex', gap: '12px', whiteSpace: 'nowrap' }}>
                                    <p style={{ margin: 0 }}><i className="fas fa-question-circle" /> {paper.questions.length} Questions</p>
                                    <p style={{ margin: 0 }}><i className="fas fa-clock" /> {paper.timeLimitMinutes || 'No'} min</p>
                                  </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                                  {canUserManageContent(user, paper) && (
                                    <div className={viewMode === 'details' ? "" : "management-overlay"} style={viewMode === 'details' ? { display: 'flex', gap: '4px' } : {}}>
                                      <button className="premium-icon-btn" style={viewMode === 'details' ? { padding: '4px', fontSize: '0.8rem' } : {}} onClick={() => onEditContent({ type: 'exam', item: paper })} title="Edit Exam Paper">
                                        <i className="fas fa-pencil-alt" />
                                      </button>
                                      <button className="premium-icon-btn" style={viewMode === 'details' ? { padding: '4px', fontSize: '0.8rem' } : {}} onClick={() => handleToggleReleased('Exam', paper)} title={paper.released ? "Unrelease Exam" : "Release Exam"}>
                                        <i className={`fas fa-${paper.released ? 'check-circle' : 'circle'}`} />
                                      </button>
                                      {canUserDeleteContent(user, paper) && (
                                        <button className="premium-icon-btn danger" style={viewMode === 'details' ? { padding: '4px', fontSize: '0.8rem' } : {}} onClick={() => handleDelete('Exam', paper)} title="Delete Exam Paper">
                                          <i className="fas fa-trash" />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  <button className={viewMode === 'details' ? "secondary submit-btn-small" : "secondary"} style={viewMode === 'details' ? { margin: 0, padding: '6px 12px', flexShrink: 0, background: 'transparent', color: 'var(--primary)', borderColor: 'var(--primary)' } : { marginTop: '20px', background: 'transparent', color: 'var(--primary)', borderColor: 'var(--primary)' }} onClick={() => onTakeTest(paper._id)}>
                                    {paper.preferPdf ? (
                                      <><i className="fas fa-file-pdf" style={{ marginRight: '8px' }} /> View PDF</>
                                    ) : (
                                      'Take Test'
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      } else {
                        const visibleFiles = getVisibleFiles(paper);
                        if (viewMode === 'list') {
                          return (
                            <div
                              key={paper._id}
                              className="drag-wrapper"
                              style={wrapperStyle}
                              onDragOver={(e) => handleDragOverItem(e, paper, 'paper')}
                              onDragLeave={handleDragLeaveItem}
                              onDrop={(e) => handleDropItem(e, paper, 'paper')}
                            >
                              <div 
                                className="premium-card" 
                                onClick={() => visibleFiles.length > 0 && onViewDocument(paper._id)}
                                style={{ 
                                  cursor: 'pointer', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  padding: '6px 10px', 
                                  gap: '8px', 
                                  flexWrap: 'nowrap', 
                                  maxWidth: 'none', 
                                  background: 'var(--bg-card)', 
                                  borderRadius: '6px', 
                                  border: '1px solid var(--border-light)',
                                  ...dragStyle 
                                }}
                                title={`Open Document: ${paper.title}`}
                                draggable={isDraggable}
                                onDragStart={(e) => handleDragStartPaper(e, 'Document', paper)}
                                onDragEnd={handleDragLeaveItem}
                              >
                                <i className="fas fa-file-alt" style={{ opacity: 0.7, color: 'var(--blue)', flexShrink: 0, fontSize: '0.95rem' }} />
                                <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, fontWeight: '500' }}>
                                  <MathText key={paper.title} text={paper.title} />
                                  {['super_user', 'college_admin', 'teacher'].includes(user?.role) && (
                                    <span style={{ marginLeft: '8px', fontSize: '0.75rem', backgroundColor: '#6c757d', color: '#fff', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>{paper.views || 0}</span>
                                  )}
                                </span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={paper._id}
                            className="drag-wrapper"
                            style={wrapperStyle}
                            onDragOver={(e) => handleDragOverItem(e, paper, 'paper')}
                            onDragLeave={handleDragLeaveItem}
                            onDrop={(e) => handleDropItem(e, paper, 'paper')}
                          >
                            <div 
                              className="premium-card" 
                              style={{
                                ...(viewMode === 'details' ? { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', gap: '16px', flexWrap: 'nowrap', maxWidth: 'none' } : {}),
                                ...dragStyle
                              }}
                              draggable={isDraggable}
                              onDragStart={(e) => handleDragStartPaper(e, 'Document', paper)}
                              onDragEnd={handleDragLeaveItem}
                            >
                              <div style={viewMode === 'details' ? { flex: 1, minWidth: 0 } : {}}>
                                {viewMode === 'grid' && <div className="card-type-badge card-type-doc">Document</div>}
                                <div style={{ fontSize: viewMode === 'details' ? '0.95rem' : '1.05rem', margin: viewMode === 'details' ? '0' : '0 0 8px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: viewMode === 'details' ? 'nowrap' : 'normal', fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                                  <MathText key={paper.title} text={paper.title} />
                                  {['super_user', 'college_admin', 'teacher'].includes(user?.role) && (
                                    <span style={{ marginLeft: '8px', fontSize: '0.75rem', backgroundColor: '#6c757d', color: '#fff', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>{paper.views || 0}</span>
                                  )}
                                </div>
                              </div>
                              <div style={viewMode === 'details' ? { display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 } : {}}>
                                {viewMode === 'details' && (
                                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', display: 'flex', gap: '12px', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                    {paper.college && (
                                      <span style={{ fontSize: '0.6rem', background: 'rgba(79, 70, 229, 0.1)', color: 'var(--primary)', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', textTransform: 'uppercase', flexShrink: 0 }}>
                                        COLLEGE
                                      </span>
                                    )}
                                    <p style={{ margin: 0 }}><i className="fas fa-file-alt" /> {paper.type}</p>
                                    <p style={{ margin: 0 }}><i className="fas fa-globe" /> {paper.visibility}</p>
                                  </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                                  {canUserManageContent(user, paper) && (
                                    <div className={viewMode === 'details' ? "" : "management-overlay"} style={viewMode === 'details' ? { display: 'flex', gap: '4px' } : {}}>
                                      <button className="premium-icon-btn" style={viewMode === 'details' ? { padding: '4px', fontSize: '0.8rem' } : {}} onClick={() => onEditContent({ type: 'document', item: paper })} title="Edit Document">
                                        <i className="fas fa-pencil-alt" />
                                      </button>
                                      <button className="premium-icon-btn" style={viewMode === 'details' ? { padding: '4px', fontSize: '0.8rem' } : {}} onClick={() => handleToggleReleased('Document', paper)} title={paper.released ? "Unrelease Document" : "Release Document"}>
                                        <i className={`fas fa-${paper.released ? 'check-circle' : 'circle'}`} />
                                      </button>
                                      {canUserDeleteContent(user, paper) && (
                                        <button className="premium-icon-btn danger" style={viewMode === 'details' ? { padding: '4px', fontSize: '0.8rem' } : {}} onClick={() => handleDelete('Document', paper)} title="Delete Document">
                                          <i className="fas fa-trash" />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  {visibleFiles.length > 0 && (
                                    <a href={`/document/${paper._id}`} onClick={(e) => { e.preventDefault(); onViewDocument(paper._id); }} className="link-button" style={viewMode === 'details' ? { margin: 0, padding: '6px 12px', fontWeight: '600', flexShrink: 0 } : { marginTop: '20px', textAlign: 'center', fontWeight: '600', display: 'block' }}>
                                      {visibleFiles.length > 1 ? 'View Docs' : 'View Document'}
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>

                  {!hasContent && subfolders.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '100px', background: 'rgba(0,0,0,0.02)', borderRadius: 'var(--radius-lg)' }}>
                      <i className="fas fa-inbox fa-3x" style={{ opacity: 0.1, marginBottom: '20px' }} />
                      <p style={{ color: 'var(--text-muted)' }}>This folder is empty</p>
                    </div>
                  )}
                </>
              )}
            </div>
            )}
          </main>
        </div>
      </div>
    </div>

    {/* ── Bulk Upload Modal (super_user only) ── */}
    {showBulkUpload && (
      <BulkUploadModal
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        folders={folders}
        initialFolderId={selectedFolder?._id || ''}
        onSuccess={async (created) => {
          // Refresh documents list after successful bulk upload
          try {
            const dRes = await api.get('/documents');
            if (Array.isArray(dRes.data)) setDocuments(dRes.data);
          } catch (_) {}
          setShowBulkUpload(false);
        }}
      />
    )}
    </>
  );
};

export default BrowsePage;
